import {Stack} from '@mui/material';
import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {
	AbandonFlowModal,
	AlertActions,
	AlertModal,
	MainFooter,
	MainHeader,
	StepProgressBar,
	StyledGrid,
	SwipeableEdgeDrawer,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {useIsAutocompleteFocusedStore} from 'presentation/components/Filter/store/useIsAutocompleteFocusedStore';
import {OurDoctorSpecialtyAlert} from 'presentation/components/OurDoctor/SpecialtyAlert';
import {
	FavoritesEnum,
	FeaturesEnum,
	HealthBookingEnum,
	HomeEnum,
	QueryParamsEnum,
} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getSearchPlaceholder} from 'presentation/utils';
import {SearchBar, SummarySectionGroup} from '..';
import {FlowEnum} from '../../constants';
import {useQueryParamsSync} from '../../hooks/useQueryParamsSync';
import {BookingSteps} from '../../pages/BookingSteps';

import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';

import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {ChangeDialogsGroup} from '../ChangeDialogsGroup';
import {HEADER_TITLES, STEPS_TO_RESET_UNIT_LIST} from './constants';
import {StyledBox, StyledContent} from './styles';
import {BookingFlowLayoutProps} from './types';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {ScheduleScriptV2} from '../../pages/ScheduleScriptV2';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {QualityCertificationBanner} from 'presentation/modules/booking/telehealth/ui/components/QualityCertificationBanner';
import {ONLY_TELEMEDICINE_SPECIALTIES_CODE} from 'presentation/modules/booking/shared/constants/onlyTelemedicineSpecialties';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {TypeLocalNameEnum} from 'domain/usecases';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';

const {appointmentSummary, abandonFlow, scheduleScript, searchSpecialty, backFlow} =
	translations['pt-br'].privateFlow.bookingFlow;

export function BookingFlowLayout({stepLabels, headerTitle, flowType}: BookingFlowLayoutProps) {
	const stepsVisited = useRef(new Set<number>());
	const [isFirstVisit, setIsFirstVisit] = useState<boolean>(true);
	const [openScriptV2BackModal, setOpenScriptV2BackModal] = useState(false);

	const {
		activeBookingFlowStep,
		bookingData,
		setActiveBookingFlowStep,
		setBookingData,
		setSearchParams,
		clearSearch,
	} = useQueryParamsSync({isFirstVisit});

	const hideSearchBar = useHideSearchBarStore(state => state.hideSearchBar);
	const setHideSearchBar = useHideSearchBarStore(state => state.setHideSearchBar);
	const hideStepBar = useHideStepBarStore(state => state.hideStepBar);
	const setHideStepBar = useHideStepBarStore(state => state.setHideStepBar);
	const autocompleteFocused = useIsAutocompleteFocusedStore(state => state.isAutocompleteFocused);
	const setAutocompleteFocused = useIsAutocompleteFocusedStore(
		state => state.setIsAutocompleteFocused,
	);
	const {isDesktop, isLaptop, isMobile} = useMediaQueries();
	const navigate = useNavigate();
	const resetUnitResponseState = useUnitsListStore(state => state.resetUnitsList);
	const {isOurDoctorPlan} = useAccountInformation();

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isExam = flowType === FlowEnum.EXAM;
	const isHealth = flowType === FlowEnum.HEALTH;

	const isUsingGeolocation = !bookingData.isManualLocation && (isExam || isHealth || isOdonto);

	const shouldResetUnitList = () => {
		if (isTelehealth) return false;

		if (isOdonto) return STEPS_TO_RESET_UNIT_LIST.odonto.includes(activeBookingFlowStep);

		if (isExam) return STEPS_TO_RESET_UNIT_LIST.exam.includes(activeBookingFlowStep);

		if (isHealth) return STEPS_TO_RESET_UNIT_LIST.health.includes(activeBookingFlowStep);

		return false;
	};

	const {
		handleChangeModals,
		openDrawerState,
		setOpenDrawerState,
		abandonFlowState,
		setAbandonFlowState,
	} = useBookingSummary();

	const {isEnabled: scheduleScriptFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULE_SCRIPT,
	);
	const {isEnabled: ourDoctorFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NOSSO_MEDICO,
	);
	const {isEnabled: isOnlyTelemedicineSpecialtyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SPECIALTY_ONLY_TELEMEDICINE_CHIP,
	);
	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const isScheduleScriptActiveFlag =
		scheduleScriptFlagIsEnabled !== null &&
		scheduleScriptFlagIsEnabled &&
		bookingData.isScheduleScript;

	const isLastStep = activeBookingFlowStep === stepLabels.length - 1;
	const shouldUseNewResumeLayout = isNewResumePageEnabled && isLastStep;

	const SEARCH_PLACEHOLDER: Record<number, string> = getSearchPlaceholder(
		flowType,
		isUsingGeolocation,
	);

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		DISTRICT_STEP,
	} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	const shouldShowOurDoctorAlert =
		ourDoctorFlagIsEnabled &&
		isOurDoctorPlan &&
		(isHealth || isTelehealth) &&
		activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP &&
		!isScheduleScriptActiveFlag;

	const shouldHideSearchBarAndBookingSummary =
		activeBookingFlowStep === LOCATION_STEP && (isHealth || isExam || isOdonto);

	const shouldHideSearchBar =
		hideSearchBar ||
		isLastStep ||
		isScheduleScriptActiveFlag ||
		shouldHideSearchBarAndBookingSummary;

	const showSwipeableDrawerBookingSummary =
		!isLastStep && isLaptop && !shouldHideSearchBarAndBookingSummary;

	const shouldBacktoHealthFlow =
		activeBookingFlowStep === PROFESSIONAL_STEP &&
		ONLY_TELEMEDICINE_SPECIALTIES_CODE.includes(bookingData.specialtyCode) &&
		bookingData.initialBookingLocationData?.bookingType === BookingTypeEnum.HEALTH &&
		isOnlyTelemedicineSpecialtyEnabled;

	const handleBackToHealthFlow = () => {
		const healthStepPositions = getHealthStepPosition({
			isManualLocation:
				bookingData.initialBookingLocationData?.typeLocalName !==
				TypeLocalNameEnum.COORDENADAS,
		});

		setBookingData(prev => ({
			...prev,
			unitCode: '',
			unitName: '',
			unitAddress: '',
			ufCode: bookingData.initialBookingLocationData?.ufCode,
			ufName: bookingData.initialBookingLocationData?.ufName,
			cityName: bookingData.initialBookingLocationData?.cityName,
			coordinates: bookingData.initialBookingLocationData?.coordinates,
			bookingType: BookingTypeEnum.HEALTH,
			hourAt: '',
			dateAt: '',
			specialtyCode: '',
			subSpecialtyCode: '',
			specialtyName: '',
			professionalCode: '',
			professionalName: '',
			isManualLocation:
				bookingData.initialBookingLocationData?.typeLocalName === TypeLocalNameEnum.CIDADE,
			typeLocalName:
				bookingData.initialBookingLocationData?.typeLocalName || TypeLocalNameEnum.CIDADE,
			initialBookingLocationData: undefined,
			isRebooking: false,
		}));
		setActiveBookingFlowStep(healthStepPositions.SPECIALITY_OR_EXAM_STEP);
		navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
	};

	const handleClose = useCallback(() => {
		setAbandonFlowState(true);
	}, []);

	const handleCloseAutocompleteOptions = () => {
		setAutocompleteFocused(false);
	};

	const getBookingOrigin = () => {
		if (isHealth) return BookingOriginEnum.BOOKING_HEALTH;
		if (isTelehealth) return BookingOriginEnum.BOOKING_TELEHEALTH;
		if (isOdonto) return BookingOriginEnum.BOOKING_ODONTO;
		if (isExam) return BookingOriginEnum.BOOKING_EXAM;
		return undefined;
	};

	const handleBackHealthAndExam = useCallback(() => {
		clearSearch();

		if (activeBookingFlowStep === LOCATION_STEP) {
			handleClose();
			return;
		}

		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			setBookingData(prev => ({
				...prev,
				isManualLocation: true,
			}));
		}

		if (activeBookingFlowStep === DISTRICT_STEP) {
			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				bookingNumber: '',
				specialtyCode: '',
				subSpecialtyCode: '',
				specialtyName: '',
				isScheduleScript: false,
				districtName: '',
				allDistrictsSelected: false,
				citiesNames: '',
				bookingOrigin: getBookingOrigin(),
			}));
		}

		if (activeBookingFlowStep === UNIT_STEP) {
			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
					return prev;
				},
				{
					replace: true,
				},
			);

			if (bookingData?.isFavoriteDoctorsFlow) {
				const favoritesUrl = bookingData.specialtyName
					? `${FavoritesEnum.FAVORITE_DOCTORS}?specialty=${encodeURIComponent(bookingData.specialtyName)}`
					: FavoritesEnum.FAVORITE_DOCTORS;
				navigate(favoritesUrl, {replace: true});
				return;
			}

			if (bookingData.shouldBackToSpecialtyStep) {
				setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
				return;
			}
		}

		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			setBookingData(prev => ({
				...prev,
				unitCode: '',
				unitAddress: '',
				unitName: '',
				dateAt: '',
			}));
		}
		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: prev?.isFavoriteDoctorsFlow ? prev.professionalCode : '',
				professionalName: prev?.isFavoriteDoctorsFlow ? prev.professionalName : '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);

					return prev;
				},
				{
					replace: true,
				},
			);
		}

		setActiveBookingFlowStep(value => value - 1);
	}, [
		activeBookingFlowStep,
		isHealth,
		bookingData.isScheduleScript,
		bookingData.isFavoriteDoctorsFlow,
		bookingData.specialtyName,
		isScheduleScriptActiveFlag,
	]);

	const handleBackTelehealth = () => {
		clearSearch();
		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			handleClose();
			return;
		}
		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			if (shouldBacktoHealthFlow) {
				handleBackToHealthFlow();
				return;
			}

			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				bookingNumber: '',
				specialtyCode: '',
				subSpecialtyCode: '',
				specialtyName: '',
				multipleBookings: false,
				isScheduleScript: false,
				bookingOrigin: getBookingOrigin(),
			}));
		}
		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: prev?.isFavoriteDoctorsFlow ? prev.professionalCode : '',
				professionalName: prev?.isFavoriteDoctorsFlow ? prev.professionalName : '',
				initialBookingLocationData: undefined,
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);

					return prev;
				},
				{
					replace: true,
				},
			);
		}

		setActiveBookingFlowStep(value => value - 1);
	};

	const handleBackOdonto = useCallback(() => {
		clearSearch();
		if (activeBookingFlowStep === LOCATION_STEP) {
			handleClose();
			return;
		}

		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			setBookingData(prev => ({
				...prev,
				isManualLocation: true,
			}));
		}
		if (activeBookingFlowStep === DISTRICT_STEP) {
			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				bookingNumber: '',
				specialtyCode: '',
				subSpecialtyCode: '',
				specialtyName: '',
				allDistrictsSelected: false,
			}));
		}
		if (activeBookingFlowStep === UNIT_STEP) {
			setBookingData(prev => ({
				...prev,
				odontoRecommendations: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.DISABILITY);
					prev.delete(QueryParamsEnum.INCLUDE_NEIGHBORHOOD);
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);

					return prev;
				},
				{
					replace: true,
				},
			);
		}
		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			setBookingData(prev => ({
				...prev,
				unitCode: '',
				unitAddress: '',
				odontoUnitDistrictSelected: '',
				unitName: '',
				dateAt: '',
			}));
		}
		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);

					return prev;
				},
				{
					replace: true,
				},
			);
		}
		setActiveBookingFlowStep(value => value - 1);
	}, [activeBookingFlowStep]);

	const handleBackScheduleScriptV2Opened = useCallback(() => {
		setOpenScriptV2BackModal(true);
	}, []);

	const handleBackScheduleScriptV2Confirmed = useCallback(() => {
		setOpenScriptV2BackModal(false);
		handleBackHealthAndExam();
	}, [handleBackHealthAndExam]);

	const handleBackScheduleScriptV2Cancelled = useCallback(() => {
		setOpenScriptV2BackModal(false);
	}, []);

	const handleBack = () => {
		if (isOdonto) return handleBackOdonto();
		if (isTelehealth) return handleBackTelehealth();

		if (isHealth && bookingData.isScheduleScript && isScheduleScriptActiveFlag) {
			return handleBackScheduleScriptV2Opened();
		}

		return handleBackHealthAndExam();
	};

	const handleNext = () => {
		if (activeBookingFlowStep === stepLabels.length) {
			return;
		}
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});
		if (shouldResetUnitList()) {
			resetUnitResponseState();
		}
		if (isUsingGeolocation && activeBookingFlowStep < UNIT_STEP) {
			setBookingData(prev => ({
				...prev,
				hasGeoDistrictsFilter: false,
			}));
		}

		setActiveBookingFlowStep(value => value + 1);
	};

	const handleConfirmExitFlow = useCallback(() => {
		setAbandonFlowState(false);
		navigate(HomeEnum.HOME_PAGE);
	}, []);

	const handleHeaderTitle = () => {
		if (bookingData.isScheduleScript) {
			return scheduleScript.title;
		}
		if (!isMobile) {
			return headerTitle;
		}
		if (isTelehealth) {
			return HEADER_TITLES.telehealth[activeBookingFlowStep];
		}
		if (isOdonto) {
			return isUsingGeolocation
				? HEADER_TITLES.odonto.withGeo[activeBookingFlowStep]
				: HEADER_TITLES.odonto.withoutGeo[activeBookingFlowStep];
		}
		if (isExam) {
			return isUsingGeolocation
				? HEADER_TITLES.exam.withGeo[activeBookingFlowStep]
				: HEADER_TITLES.exam.withoutGeo[activeBookingFlowStep];
		}
		return isUsingGeolocation
			? HEADER_TITLES.health.withGeo[activeBookingFlowStep]
			: HEADER_TITLES.health.withoutGeo[activeBookingFlowStep];
	};

	const descriptionAbandonModal = {
		[FlowEnum.TELEHEALTH]: abandonFlow.teleDescription,
		[FlowEnum.HEALTH]: abandonFlow.description,
		[FlowEnum.ODONTO]: abandonFlow.description,
		[FlowEnum.EXAM]: abandonFlow.examDescription,
	};

	const shouldHideBackButton = () => {
		if (!bookingData.isRebooking) return false;

		if (isOdonto && activeBookingFlowStep === DISTRICT_STEP) return true;

		if (isTelehealth && activeBookingFlowStep === PROFESSIONAL_STEP) {
			if (shouldBacktoHealthFlow) return false;

			return true;
		}

		if ((isHealth || isExam) && activeBookingFlowStep === DISTRICT_STEP) return true;

		return false;
	};

	const customFooterMarginBottom = () => {
		if (isUsingGeolocation) return undefined;
		if ((isHealth || isExam) && activeBookingFlowStep === DISTRICT_STEP && isMobile) {
			return '142px';
		}

		return undefined;
	};

	const shouldShowSpecialtyTagging =
		SEARCH_PLACEHOLDER[activeBookingFlowStep] === searchSpecialty && (isHealth || isTelehealth);

	useEffect(() => {
		const isFirstVisitValue = !stepsVisited.current.has(activeBookingFlowStep);
		setIsFirstVisit(isFirstVisitValue);
		stepsVisited.current.add(activeBookingFlowStep);
	}, [activeBookingFlowStep]);

	useEffect(() => {
		setHideSearchBar(false);
		setHideStepBar(false);
		clearSearch();
	}, [activeBookingFlowStep]);

	return (
		<Fragment>
			<MainHeader
				title={handleHeaderTitle()}
				dataTagId={{
					backButton: isScheduleScriptActiveFlag
						? AppointmentBookingEnum.CLICK_VOLTAR_SCRIPT
						: AppointmentBookingEnum.CLICK_VOLTAR_AGENDAMENTO,
					closeButton: isScheduleScriptActiveFlag
						? AppointmentBookingEnum.CLICK_FECHAR_SCRIPT
						: AppointmentBookingEnum.CLICK_DESCARTAR_AGENDAMENTO,
				}}
				handleBack={autocompleteFocused ? handleCloseAutocompleteOptions : handleBack}
				handleClose={autocompleteFocused ? handleCloseAutocompleteOptions : handleClose}
				hideBackBtn={shouldHideBackButton()}
				variant="subRoute">
				{!isScheduleScriptActiveFlag && !hideStepBar ? (
					<StyledGrid.Root px={isMobile ? 2 : 4} pb={3}>
						<StyledGrid.Item sm={12} md={12} lg={12}>
							<Stack gap={3}>
								{isMobile ? (
									<StepProgressBar
										mt={1}
										currentStep={activeBookingFlowStep + 1}
										totalSteps={stepLabels.length}
									/>
								) : (
									<CustomStepper
										dataTagId={AppointmentBookingEnum.NOME_STEP}
										activeStep={activeBookingFlowStep}
										stepLabels={stepLabels}
									/>
								)}
							</Stack>
						</StyledGrid.Item>
					</StyledGrid.Root>
				) : null}
			</MainHeader>
			{shouldHideSearchBar ? null : (
				<Fragment>
					<SearchBar
						activeStep={activeBookingFlowStep}
						flowType={flowType}
						placeholder={SEARCH_PLACEHOLDER[activeBookingFlowStep]}
						searchDataTagId={
							shouldShowSpecialtyTagging
								? AppointmentBookingEnum.CLICK_BUSCAR_ESPECIALIDADE
								: undefined
						}
					/>
					{shouldShowOurDoctorAlert ? <OurDoctorSpecialtyAlert /> : null}
				</Fragment>
			)}
			{isScheduleScriptActiveFlag ? (
				<ScheduleScriptV2 handleNext={handleNext} />
			) : (
				<Fragment>
					<StyledGrid.Root p={isMobile ? 2 : 4} height="100%">
						<StyledGrid.Item
							sm={12}
							md={12}
							lg={
								shouldHideSearchBarAndBookingSummary || shouldUseNewResumeLayout
									? 12
									: 8
							}>
							<BookingSteps
								activeStep={activeBookingFlowStep}
								flowType={flowType}
								handleBack={handleBack}
								handleNext={handleNext}
								stepLabels={stepLabels}
							/>
						</StyledGrid.Item>
						{isDesktop &&
						!shouldHideSearchBarAndBookingSummary &&
						!shouldUseNewResumeLayout ? (
							<StyledGrid.Item md={4} lg={4}>
								<SummarySectionGroup
									handleBack={handleBack}
									activeStep={activeBookingFlowStep}
									flowType={flowType}
									handleChangeModals={handleChangeModals}
									stepLabels={stepLabels}
								/>
							</StyledGrid.Item>
						) : null}
					</StyledGrid.Root>

					<StyledBox
						$isLastStep={isLastStep}
						$adjustFooterMargin={shouldHideSearchBarAndBookingSummary}>
						{isTelehealth ? <QualityCertificationBanner /> : null}
						<MainFooter customMarginBottom={customFooterMarginBottom()} />
					</StyledBox>
					{showSwipeableDrawerBookingSummary ? (
						<SwipeableEdgeDrawer
							type="with-appbar"
							title={appointmentSummary}
							open={openDrawerState}
							setOpen={setOpenDrawerState}
							customDrawerHeight={openDrawerState ? 'auto' : undefined}>
							<StyledContent>
								<SummarySectionGroup
									activeStep={activeBookingFlowStep}
									handleBack={handleBack}
									flowType={flowType}
									handleChangeModals={handleChangeModals}
									stepLabels={stepLabels}
									showTitle={false}
									disableGutters={true}
								/>
							</StyledContent>
						</SwipeableEdgeDrawer>
					) : null}
				</Fragment>
			)}

			<AlertModal
				open={openScriptV2BackModal}
				title={backFlow.title}
				variant="info"
				headerTitle={backFlow.headerTitle}
				description={backFlow.description}
				onClose={handleBackScheduleScriptV2Cancelled}
				headerDataTagId={AppointmentBookingEnum.VISUALIZOU_MODAL_AVISO_RETORNO_SCRIPT}
				closeBtnDataTagId={AppointmentBookingEnum.CLICK_FECHAR_MODAL_AVISO_RETORNO_SCRIPT}
				footerContent={
					<AlertActions
						primaryText={backFlow.primary}
						primaryAction={handleBackScheduleScriptV2Confirmed}
						dataTagId={
							AppointmentBookingEnum.CLICK_CONFIRMAR_MODAL_AVISO_RETORNO_SCRIPT
						}
						secondaryText={backFlow.secondary}
						secondaryAction={handleBackScheduleScriptV2Cancelled}
						secondaryBtnDataTagId={
							AppointmentBookingEnum.CLICK_CANCELAR_MODAL_AVISO_RETORNO_SCRIPT
						}
					/>
				}
			/>
			<AbandonFlowModal
				open={abandonFlowState}
				setOpen={setAbandonFlowState}
				headerTitle={abandonFlow.headerTitle}
				title={abandonFlow.title}
				description={descriptionAbandonModal[flowType]}
				handleConfirm={handleConfirmExitFlow}
				confirmButtondataTagId={
					bookingData.isScheduleScript
						? AppointmentBookingEnum.ABANDONOU_SCRIPT_AGENDAMENTO
						: AppointmentBookingEnum.ABANDONOU_AGENDAMENTO
				}
				closeButtondataTagId={
					bookingData.isScheduleScript
						? AppointmentBookingEnum.FECHOU_MODAL_ABANDONO_SCRIPT_AGENDAMENTO
						: undefined
				}
				backButtondataTagId={
					bookingData.isScheduleScript
						? AppointmentBookingEnum.DESISTIU_DE_ABANDONAR_SCRIPT_AGENDAMENTO
						: undefined
				}
				modaldataTagId={AppointmentBookingEnum.VISUALIZOU_MODAL_ABANDONO}
			/>
			<ChangeDialogsGroup
				flowType={flowType}
				setActiveBookingFlowStep={setActiveBookingFlowStep}
			/>
		</Fragment>
	);
}
